import React from 'react';
import { Route, IndexRedirect, IndexRoute } from 'react-router';
import cookie from 'react-cookie';
import axios from 'axios';
import { ADMIN_ROOT } from 'common/variables';
import App from 'common/containers/app';
import StyleGuide from 'styleguide/components/index';

import {
  ProjectBrief, ProjectWrapup, ProjectSettings, PendingAccess, DeniedAccess,
  ApprovedAccess, RemovedAccess, Invited, Participants, ProjectWrapupIntro, ProjectTour
} from 'project';
import { Explore, ExploreProjects, ExploreCreatives } from 'explore';
import { Info, IdVerification, NotificationSettings, Billing, PayOut } from 'account';
import RequestedPaymentsTable from 'account/containers/payout/requested_payments_table';
import HistoryPaymentsTable from 'account/containers/payout/history_payments_table';
import EnablePayoneer from 'account/containers/payout/enable_payoneer';

import { Login, Logout } from 'login/containers/index';
import PasswordReset from 'login/components/password_reset';
import VerifyEmail from 'common/components/verify_email';
import VerifyPhone from 'common/components/verify_phone';
import PAP0Wrapper from 'pap0/components/wrapper';
import Categories from 'pap0/containers/categories';
import Start1to1 from 'pap0/containers/start_1_1';
import { PAPDetails, PAPBrief, PAPReview, PAPPayment } from 'pap/index';
import PAPBriefV2 from 'pap_v2/containers/brief';
import PAPBriefAllV2 from 'pap_v2/containers/brief_all';
import PAPDetailsV2 from 'pap_v2/containers/details';
import RegistrationStep1 from 'registration/common/containers/step1';
import RegistrationStep2 from 'registration/common/containers/step2';
import {
  Profile, ViewProfile, EditProfile, Portfolio, AllYourEntries, InactiveUser,
  YourImageEntries, YourNameEntries
} from 'profile';
import ErrorPage from 'error';
import Forbidden1to1 from 'error/components/forbidden_1_1';
import ProjectDown from 'error/components/project_down';
import ClientsOnly from 'error/components/clients_only';
import CreativesOnly from 'error/components/creatives_only';
import NotificationsPage from 'notifications';
import HelpPage, {
  HelpHome, HelpCategory, HelpArticle, HelpSearch, AllHelpGuides, HelpGuide, ContactHelp,
} from 'help';
import AccountRemoved from 'account/components/account_removed';
import FocusGroups, {
  ViewFocusGroup, VoteFocusGroup, FocusGroupResults, FocusGroupSettings, FocusGroupParticipants,
  FocusGroupComments, FocusGroupVotes,
} from 'focus_groups';
import NewPresentation from 'presentations/containers/new';
import Presentation from 'presentations/containers/presentation';
import PresentationResults from 'presentations/containers/results';
import PresentationParticipants from 'presentations/containers/participants';
import PresentationSettings from 'presentations/containers/settings';
import PresentationRevisionResults from 'presentations/containers/revision_results'
import PresentationRevisionComments from 'presentations/containers/revision_comments'
import PresentationRevisionVotes from 'presentations/containers/revision_votes'
import AcceptCollaborationInvite from 'project/containers/accept_collaboration_invite';
import About, { Home, Team, Jobs, Press, Partners, Affiliates } from 'marketing/about';
import HomePage from 'marketing/components/home';
import HowPage from 'marketing/how';
import CaseStudies from 'marketing/containers/case_studies';
import Legal from 'marketing/legal';
import Deprecations from 'marketing/deprecations';
import TestimonialsPage from 'marketing/containers/testimonials';
import PricingPage from 'marketing/pricing';
import ExternalCategories from 'marketing/containers/categories';
import Consultation from 'marketing/containers/consultation';
import ConsultationForAgencies from 'marketing/containers/consultation_agencies';
import ConsultationChinese from 'marketing/containers/consultation_chinese';
import LandingPage from 'landing_pages/containers';
import DiscountCode from 'common/containers/discount_code';
import SingleEntry, {
  SingleEntryIndex, SingleEntryComments, SingleEntryRevisions
} from 'single_entry';
import ProjectOrLanding from 'landing_pages/containers/wrapper';
import ReferAFriend from 'marketing/containers/refer_a_friend';
import Referral from 'marketing/containers/referral';
import ProductUpdates from 'product_updates/containers';
import Entries from 'entries/containers';
import DripCampaignSubscription from 'landing_pages/components/drip_campaign_subscription';
import YourProjects from 'your_projects/containers';
import RedeemRegistrationInvite from 'registration/creative/containers/redeem_invite';

/* intercept all requests and add CSRF header appropriately */
axios.interceptors.request.use((config) => {
  const csrfToken = cookie.load('csrftoken');
  if (csrfToken) {
    const headers = {
      ...config.headers,
      'X-CSRFToken': csrfToken,
      'X-FrontendURL': window ? window.location : '',
    };
    return {
      ...config,
      headers,
    };
  }
  return config;
});

/*
 * @param {Redux Store}
 * We require store as an argument here because we wish to get
 * state from the store after it has been authenticated.
 */
export default (store) => {
  function requireAuth(nextState, replace) {
    const state = store.getState();
    if (!state.user.logged_in) {
      replace({
        pathname: '/login',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  function requireClientOrAnon(nextState, replace) {
    const state = store.getState();
    if (state.user.logged_in && !state.user.profile_data.is_client) {
      replace({
        pathname: '/clients-only-page/',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  function requireClient(nextState, replace) {
    const state = store.getState();
    if (!state.user.logged_in || !state.user.profile_data.is_client) {
      replace({
        pathname: '/clients-only-page/',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  function requireCreative(nextState, replace) {
    const state = store.getState();
    if (!state.user.logged_in || !state.user.profile_data.is_creative) {
      replace({
        pathname: '/creatives-only-page/',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  function checkEmailValidated(nextState, replace) {
    const state = store.getState();
    if (state.user.is_email_validated) {
      replace({
        pathname: '/your-projects',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  return (
    <Route path="/" component={App}>
      <IndexRoute component={HomePage} />
      <Route path="login" component={Login} />
      <Route path="logout" component={Logout} />
      <Route path="reviews/case-studies/:pageSlug" component={LandingPage} />
      <Route path="reviews/:pageSlug" component={LandingPage} />
      <Route path="reviews" component={TestimonialsPage} />
      <Route path="how-it-works" components={HowPage} />
      <Route path="case-studies" components={CaseStudies} />
      <Route path="pricing" components={PricingPage} />
      <Route path="categories" components={ExternalCategories} />
      <Route path="about-us" component={About} pageClass="about-us">
        <IndexRoute component={Home} />
      </Route>
      <Route path="about-us/team" component={About} pageClass="team">
        <IndexRoute component={Team} />
      </Route>
      <Route path="jobs" component={About} pageClass="jobs">
        <IndexRoute component={Jobs} />
      </Route>
      <Route path="press" component={Press.Wrapper}>
        <IndexRoute component={Press.All} />
        <Route name="releases" path="releases" component={Press.Releases}>
          <Route path="releases/:releaseSlug" component={Press.Releases} />
        </Route>
        <Route name="news" path="in-the-news" component={Press.News} />
      </Route>
      <Route path="partners/:pageSlug" component={LandingPage} />
      <Route path="partners" component={About} pageClass="partners">
        <IndexRoute component={Partners} />
      </Route>
      <Route path="affiliates" component={About} pageClass="affiliates">
        <IndexRoute component={Affiliates} />
      </Route>
      <Route path="user-agreement" component={Legal.Wrapper}>
        <IndexRoute component={Legal.Agreement} />
      </Route>
      <Route path="privacy-policy" component={Legal.Wrapper}>
        <IndexRoute component={Legal.Privacy} />
      </Route>
      <Route path="copyright-policy" component={Legal.Wrapper}>
        <IndexRoute component={Legal.Copyright} />
      </Route>
      <Route path="standards-of-conduct-client" component={Legal.Wrapper}>
        <IndexRoute component={Legal.StandardsClient} />
      </Route>
      <Route path="standards-of-conduct-creative" component={Legal.Wrapper}>
        <IndexRoute component={Legal.StandardsCreative} />
      </Route>
      <Route path="refer-a-friend" components={ReferAFriend} onEnter={requireClient}/>
      <Route path="referral/:referrer" components={Referral} />
      <Route path="whats-new/:userType" components={ProductUpdates} onEnter={requireAuth}/>
      <Route
        path="verify/:verifyString/:hexString"
        component={VerifyEmail}
        onEnter={checkEmailValidated}
      />
      <Route path="verify/mobile/:verifyString/:hexString" component={VerifyPhone} />
      <Route path="reset-password/:uid/:key" component={PasswordReset} />

      <Route path="your-projects(/:userType)(/:projectType)" component={YourProjects}
        name="your-projects" onEnter={requireAuth}/>

      <Route path="account/info" component={Info} onEnter={requireAuth} />
      <Route path="account/notifications" component={NotificationSettings} onEnter={requireAuth} />
      <Route path="account/id-verification" component={IdVerification} onEnter={requireAuth} />
      <Route path="account/billing" component={Billing} onEnter={requireAuth} />
      <Route path="account/removed" component={AccountRemoved} onEnter={requireAuth} />
      <Route path="account/payments" component={PayOut} onEnter={requireAuth}>
        <IndexRedirect to="requested" />
        <Route path="requested" component={RequestedPaymentsTable} />
        <Route path="history" component={HistoryPaymentsTable} />
      </Route>

      <Route path="enable-payoneer" component={EnablePayoneer} onEnter={requireAuth} />

      <Route name="notifications" path="notifications" component={NotificationsPage}
        onEnter={requireAuth} />

      <Route
        name="vote-focus-group"
        path="focus-group-participate/:focusGroupId/:participantId/:participantKey"
        component={VoteFocusGroup}
      />

      <Route
        name="vote-focus-group"
        path="focus-group-participate/:focusGroupId/:anonymousKey"
        component={VoteFocusGroup}
      />

      <Route
        path="collaboration-invite/:projectId/:inviteId/:inviteKey"
        component={AcceptCollaborationInvite}
      />

      <Route path="explore" component={Explore}>
        <IndexRoute name="explore_projects" component={ExploreProjects} />
        <Route name="explore_creatives" path="creatives" component={ExploreCreatives} />
      </Route>

      <Route
        path="start-a-project/:categorySlug/:subCategorySlug/details/:stepName"
        component={PAPDetails}
        onEnter={requireClientOrAnon}
      />
      <Route path="start-a-project/:projectId/details/:stepName" component={PAPDetailsV2} />

      <Route path="start-a-project/:projectID/brief/:stepName" component={PAPBrief} />
      {/* TODO: remove the 'new' piece from the URL below once we no longer have PAP v1.
          Remember to update getPapUrl() in pap_v2/utils when that happens. */}
      <Route path="start-a-project/new/:projectId/brief/all" component={PAPBriefAllV2} />
      <Route path="start-a-project/new/:projectId/brief(/:stepName)" component={PAPBriefV2} />
      <Route
        path="start-a-project/:categorySlug/:subCategorySlug/brief/all"
        component={PAPBriefAllV2}
        onEnter={requireClientOrAnon}
      />
      <Route
        path="start-a-project/:categorySlug/:subCategorySlug/brief(/:stepName)"
        component={PAPBriefV2}
        onEnter={requireClientOrAnon}
      />

      <Route path="start-a-project/:projectID/review" component={PAPReview} />

      <Route path="start-a-project/:projectID/payment" component={PAPPayment} />

      <Route path="start-a-project/start-1-1" component={Start1to1}
        onEnter={requireClientOrAnon} />

      <Route path="start-a-project" component={PAP0Wrapper} onEnter={requireClientOrAnon}>
        <IndexRoute component={Categories} />
        <Route path="popular" component={Categories} />
        <Route path=":categorySlug" component={Categories} />
      </Route>

      <Route path="users/(:profileUsername)" component={Profile}>
        <IndexRoute component={ViewProfile} />
        <Route path="edit" component={EditProfile} onEnter={requireAuth} />
        <Route path="portfolio" component={Portfolio} />
        <Route path="your-entries" component={AllYourEntries}>
          <Route path="image" component={YourImageEntries} />
          <Route path="name" component={YourNameEntries} />
        </Route>
      </Route>

      <Route path="styleguide" component={StyleGuide} />
      <Route path="register" component={RegistrationStep1} />
      <Route path="register/continue" component={RegistrationStep2} onEnter={requireAuth} />


      <Route path="goodbye-forums" component={Deprecations.Wrapper}>
        <IndexRoute component={Deprecations.GoodbyeForums} />
      </Route>

      <Route path="goodbye-privatemessages" component={Deprecations.Wrapper}>
        <IndexRoute component={Deprecations.GoodbyePMs} />
      </Route>

      <Route path="/help" component={HelpPage}>
        <IndexRoute component={HelpHome} />
        <Route path="search/:searchStr" component={HelpSearch} />
        <Route path="guides/:guideSlug" component={HelpGuide} />
        <Route path="guides" component={AllHelpGuides} />
        <Route path="contact/consultation-agencies" component={ConsultationForAgencies} />
        <Route path="contact/consultation-chinese" component={ConsultationChinese} />
        <Route path="contact/consultation" component={Consultation} />
        <Route path="contact" component={ContactHelp} />
        <Route path=":categorySlug/:articleSlug" component={HelpArticle} />
        <Route path=":categorySlug" component={HelpCategory} />
      </Route>

      <Route path="partner/promo/:codeSlug" component={DiscountCode} />

      <Route path={ADMIN_ROOT} getComponent={(l, cb) => import('admin').then(m => cb(null, m.default))}>
        <IndexRedirect to="projects" />
        <Route path="projects" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminProjects))
        } />
        <Route path="project/:projectId" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminProject))
        }>
          <IndexRedirect to="overview" />
          <Route path="overview" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminProjectOverview))
          } />
          <Route path="notes" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminProjectNotes))
          } />
          <Route path="billing" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminProjectBilling))
          } />
          <Route path="entries" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminProjectEntries))
          } />
          <Route path="entry/:entryId(/:drawerType)" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminProjectSingleEntry))
          } />
          <Route path="people" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminProjectPeople))
          }>
            <IndexRedirect to="participating" />
            <Route path="participating" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminProjectParticipating))
            } />
            <Route path="pending" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminProjectNDAs))
            } />
            <Route path="approved" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminProjectNDAs))
            } />
            <Route path="denied" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminProjectNDAs))
            } />
            <Route path="invited" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminProjectInvited))
            } />
            <Route path="removed" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminProjectRemoved))
            } />
            <Route path="watchers" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminProjectWatchers))
            } />
            <Route path="reservations" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminProjectReservations))
            } />
            <Route path="ranking" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminProjectRanking))
            } />
          </Route>
          <Route path="brief" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminProjectBrief))
          } />
          <Route path="wrapup/:awardId" component={ProjectWrapup} />
          <Route path="activity" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminProjectActivity))
          } />
        </Route>

        <Route path="people/unverified" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminUnverified))
        } />
        <Route path="people/clients" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminClients))
        } />
        <Route path="people/creatives" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminCreatives))
        } />
        <Route path="people/waitlist" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminWaiters))
        } />

        <Route path="client/:userId" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminUser))
        }>
          <IndexRedirect to="overview/" />
          <Route path="overview" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminClientOverview))
          } />
          <Route path="projects" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminClientProjects))
          }>
            <IndexRedirect to="active/" />
            <Route path="drafts" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminClientDraftProjects))
            } />
            <Route path="active" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminClientActiveProjects))
            } />
            <Route path="wrapup" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminClientAwardedProjects))
            } />
            <Route path="completed" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminClientCompletedProjects))
            } />
          </Route>
          <Route path="payments" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminClientPayments))
          }>
            <IndexRedirect to="all/" />
            <Route path="all" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminClientAllPayments))
            } />
            <Route path="refunded" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminClientRefundedPayments))
            } />
          </Route>
          <Route path="profile" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminProfile))
          } />
          <Route path="notifications" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminUserNotifications))
          } />
          <Route path="activity" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminUserActivity))
          } />
        </Route>

        <Route path="creative/:userId" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminUser))
        }>
          <IndexRedirect to="overview/" />
          <Route path="overview" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminCreativeOverview))
          } />
          <Route path="entries" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminUserEntries))
          } />
          <Route path="projects" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminCreativeProjects))
          }>
            <IndexRedirect to="participating/" />
            <Route path="participating" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminCreativeParticipatingProjects))
            } />
            <Route path="wrapup" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminCreativeWrapupProjects))
            } />
            <Route path="awarded" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminCreativeAwardedProjects))
            } />
            <Route path="history" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminCreativeHistoryProjects))
            } />
          </Route>
          <Route path="profile" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminProfile))
          } />
          <Route path="notifications" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminUserNotifications))
          } />
          <Route path="activity" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminUserActivity))
          } />
          <Route path="payments" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminCreativePayments))
          }>
            <IndexRedirect to="requested/" />
            <Route path="requested" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminCreativeRequestedPayments))
            } />
            <Route path="history" getComponent={
              (l, cb) => import('admin').then(m => cb(null, m.AdminCreativeHistoryPayments))
            } />
          </Route>
          <Route path="reputation" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminCreativeReputation))
          } />
          <Route path="quality-review" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.AdminQualityReview))
          } />
        </Route>

        <Route path="creative-verification/:userId" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminUser))
        } />

        <Route path="billing" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminPayments))
        } />

        <Route path="batches" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminBatches))
        } />

        <Route path="payment/:paymentId" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.SinglePayment))
        }>
          <IndexRedirect to="overview" />
          <Route path="overview" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.PaymentOverview))
          } />
          <Route path="notes" getComponent={
            (l, cb) => import('admin').then(m => cb(null, m.PaymentNotes))
          } />
        </Route>

        <Route path="reviews" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminReviews))
        } />
        <Route path="review/:reviewId" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminReview))
        } />

        <Route path="marketing" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.AdminMarketing))
        } />

        <Route path="honeybee/emails/:status" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.HoneybeeEmails))
        } />

        <Route path="honeybee/email/:id" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.HoneybeeEmail))
        } />

        <Route path="bumblebee/automations/:status" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.BumblebeeAutomations))
        } />

        <Route path="bumblebee/automation/:id" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.BumblebeeAutomation))
        } />

        <Route path="bumblebee/groups" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.BumblebeeGroups))
        } />

        <Route path="bumblebee/group/:groupId/automations/:status" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.BumblebeeAutomations))
        } />

        <Route path="bumblebee/group/:id" getComponent={
          (l, cb) => import('admin').then(m => cb(null, m.BumblebeeGroup))
        } />
      </Route>

      <Route path="inactive-user" component={InactiveUser} />
      <Route path="error" component={ErrorPage} status={500} />
      <Route path="404" component={ErrorPage} status={404} />
      <Route path="forbidden-private-project" component={Forbidden1to1} />
      <Route path="project-down-for-nap" component={ProjectDown} />
      <Route path="clients-only-page" component={ClientsOnly} />
      <Route path="creatives-only-page" component={CreativesOnly} />
      <Route path="subscribe-to-drip-campaign" component={DripCampaignSubscription} />
      <Route path="redeem-registration-invite/:hash" component={RedeemRegistrationInvite} />

      <Route path="/:subcategory/:pageSlug" component={ProjectOrLanding}>
        {/* Brief view is used as the index because it'll always be present for all possible
            project variations. */}
        <IndexRoute name="brief" component={ProjectBrief} />
        <Route name="entries" path="entries" component={Entries} />

        <Route name="single" path="entry/:entryId" component={SingleEntry}>
          <IndexRoute name="singleEntry" component={SingleEntryIndex} />
          <Route name="singleComments" path="comments" component={SingleEntryComments}
            onEnter={requireAuth} />
          <Route name="singleRevisions" path="revisions" component={SingleEntryRevisions} />
        </Route>

        <Route name="your-entries" path="your-entries" component={Entries}
          onEnter={requireCreative} />

        <Route name="wrapup" path="wrapup/intro" component={ProjectWrapupIntro}
          onEnter={requireAuth} />
        <Route name="wrapup" path="wrapup/:awardId" component={ProjectWrapup}
          onEnter={requireAuth} />

        <Route name="focus-groups-new" path="focus-groups/new" component={FocusGroups}
          onEnter={requireClient} />
        <Route
          name="view-focus-group"
          path="focus-groups/:focusGroupId/view"
          component={ViewFocusGroup}
          onEnter={requireClient}
        >
          <IndexRedirect to="results" />
          <Route name="focus-group-results" path="results" component={FocusGroupResults}>
            <IndexRedirect to="comment" />
            <Route name="focus-group-comment" path="comment" component={FocusGroupComments} />
            <Route name="focus-group-votes" path="votes" component={FocusGroupVotes} />
          </Route>
          <Route name="focus-group-settings" path="settings" component={FocusGroupSettings} />
          <Route
            name="focus-group-participants"
            path="participants"
            component={FocusGroupParticipants}
          />
        </Route>
        <Route name="presentations-new" path="presentations/new" component={NewPresentation}
          onEnter={requireClient}/>
        <Route path="presentations/:presentationId" component={Presentation} onEnter={requireClient}>
          <IndexRedirect to="results" />
          <Route path="results" component={PresentationResults}>
            <Route path=":revisionId" component={PresentationRevisionResults}>
              <IndexRedirect to="comments" />
              <Route path="comments" component={PresentationRevisionComments} />
              <Route path="votes" component={PresentationRevisionVotes} />
            </Route>
          </Route>
          <Route path="settings" component={PresentationSettings}/>
          <Route path="participants" component={PresentationParticipants}/>
        </Route>

        <Route name="settings" path="settings" component={ProjectSettings} onEnter={requireClient}>
          <Route name="pending" path="pending" component={PendingAccess} />
          <Route name="approved" path="approved" component={ApprovedAccess} />
          <Route name="denied" path="denied" component={DeniedAccess} />
          <Route name="invites" path="invites" component={Invited} />
          <Route name="removed" path="removed" component={RemovedAccess} />
          <Route name="participants" path="participants" component={Participants} />
        </Route>

        <Route path="tour" component={ProjectTour} onEnter={requireClient} />
      </Route>
      <Route path=":pageSlug" component={LandingPage} />
      <Route path="*" component={ErrorPage} status={404} />
    </Route>
  );
};
/*
      <Route path="logo-design/university-logos/:pageSlug" component={LandingPage} />
      <Route path="logo-design/:pageSlug" component={LandingPage} />
      <Route path="naming-a-business-or-product/:pageSlug" component={LandingPage} />
      */



// WEBPACK FOOTER //
// ./routes.jsx